import { PLAN_LIMITS } from '@bill-alps/backend/shared/billing/planLimits';
import {
  INVOICE_IMPORT_AVAILABLE,
  LEDGER_AVAILABLE,
} from '@/lib/runtime/feature-flags';
import type { BillingModuleKey, Plan } from './billing-package';

export { priceFor } from './billing-package';

export type Feature = {
  key: string;
  params?: Record<string, number>;
  includedPlan?: Plan;
};

export interface FeatureGroup {
  module: BillingModuleKey;
  features: Feature[];
}

export interface PlanFeatures {
  global: Feature[];
  moduleGroups: FeatureGroup[];
}

const GLOBAL_FEATURES: Record<Plan, Feature[]> = {
  free: [{ key: 'freeForLife' }],
  team: [
    { key: 'collaboration' },
    { key: 'includedPlan', includedPlan: 'free' },
  ],
  business: [
    { key: 'collaboration' },
    { key: 'includedPlan', includedPlan: 'team' },
    { key: 'api' },
    { key: 'prioritySupport' },
  ],
};

const INVOICING_FEATURES: Record<Plan, Feature[]> = {
  free: [
    { key: 'clients', params: { count: PLAN_LIMITS.free.clients } },
    { key: 'products', params: { count: PLAN_LIMITS.free.products } },
    { key: 'invoices', params: { count: PLAN_LIMITS.free.invoicesPerMonth } },
    { key: 'qrCode' },
    { key: 'pdf' },
    { key: 'reminders' },
    {
      key: 'aiPrefills',
      params: { count: PLAN_LIMITS.free.aiPrefillsPerMonth },
    },
    {
      key: 'recurringInvoices',
      params: { count: PLAN_LIMITS.free.recurringInvoiceSchedules },
    },
    ...invoiceImportFeature('free'),
  ],
  team: [
    { key: 'clientsTeam', params: { count: PLAN_LIMITS.team.clients } },
    { key: 'productsUnlimited' },
    {
      key: 'invoicesTeam',
      params: { count: PLAN_LIMITS.team.invoicesPerMonth },
    },
    { key: 'multiCurrency' },
    { key: 'autoReminders' },
    { key: 'recurringInvoicesUnlimited' },
    {
      key: 'aiPrefillsTeam',
      params: { count: PLAN_LIMITS.team.aiPrefillsPerMonth },
    },
    ...invoiceImportFeature('team'),
  ],
  business: [
    { key: 'clientsUnlimited' },
    { key: 'productsUnlimited' },
    { key: 'invoicesUnlimited' },
    { key: 'multiCurrency' },
    { key: 'autoReminders' },
    { key: 'recurringInvoicesUnlimited' },
    {
      key: 'aiPrefillsBusiness',
      params: { count: PLAN_LIMITS.business.aiPrefillsPerMonth },
    },
    ...invoiceImportFeature('business'),
  ],
};

/** Lifetime documents of the invoice import, once the feature ships. */
function invoiceImportFeature(plan: Plan): Feature[] {
  return INVOICE_IMPORT_AVAILABLE
    ? [
        {
          key: 'invoiceImport',
          params: { count: PLAN_LIMITS[plan].invoiceImportDocuments },
        },
      ]
    : [];
}

const PAYROLL_FEATURES: Record<Plan, Feature[]> = {
  free: [
    { key: 'employees', params: { count: PLAN_LIMITS.free.employees } },
    { key: 'payslips', params: { count: PLAN_LIMITS.free.payslipsPerMonth } },
  ],
  team: [
    { key: 'employeesTeam', params: { count: PLAN_LIMITS.team.employees } },
    {
      key: 'payslipsTeam',
      params: { count: PLAN_LIMITS.team.payslipsPerMonth },
    },
    { key: 'annualCertificate' },
  ],
  business: [
    { key: 'employeesUnlimited' },
    { key: 'payslipsUnlimited' },
    { key: 'annualCertificate' },
  ],
};

const ACCOUNTING_FEATURES: Record<Plan, Feature[]> = {
  free: [
    {
      key: 'supplierBills',
      params: { count: PLAN_LIMITS.free.supplierBillsPerMonth },
    },
    {
      key: 'supplierBillExtractions',
      params: { count: PLAN_LIMITS.free.supplierBillExtractionsPerMonth },
    },
    {
      key: 'bankImportLimited',
      params: { count: PLAN_LIMITS.free.bankTransactionsPerMonth },
    },
    { key: 'bankReconciliation' },
  ],
  team: [
    {
      key: 'supplierBillsTeam',
      params: { count: PLAN_LIMITS.team.supplierBillsPerMonth },
    },
    {
      key: 'supplierBillExtractionsTeam',
      params: { count: PLAN_LIMITS.team.supplierBillExtractionsPerMonth },
    },
    { key: 'bankImport' },
    { key: 'bankReconciliation' },
    ...aiCategorizationFeature(),
  ],
  business: [
    { key: 'supplierBillsUnlimited' },
    {
      key: 'supplierBillExtractionsBusiness',
      params: { count: PLAN_LIMITS.business.supplierBillExtractionsPerMonth },
    },
    { key: 'bankImport' },
    { key: 'bankReconciliation' },
    ...aiCategorizationFeature(),
  ],
};

/** AI categorisation suggests ledger accounts, so it ships with the ledger. */
function aiCategorizationFeature(): Feature[] {
  return LEDGER_AVAILABLE ? [{ key: 'aiCategorization' }] : [];
}

const FEATURES_BY_MODULE: Record<BillingModuleKey, Record<Plan, Feature[]>> = {
  invoicing: INVOICING_FEATURES,
  payroll: PAYROLL_FEATURES,
  accounting: ACCOUNTING_FEATURES,
};

/** Global benefits and feature groups for a size and the selected modules. */
export function getFeatures(
  plan: Plan,
  modules: readonly BillingModuleKey[],
): PlanFeatures {
  return {
    global: GLOBAL_FEATURES[plan],
    moduleGroups: modules.map((module) => ({
      module,
      features: FEATURES_BY_MODULE[module][plan],
    })),
  };
}
